import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PDFDocument, PDFFont, PDFPage, rgb } from 'pdf-lib';
import * as QRCode from 'qrcode';
import JSZip from 'jszip';
import { IAccessTicketVm } from '../../../api/access-ticket-vm.model';

export type InstructionLanguage = 'pl' | 'uk' | 'en';
export type InstructionFormat = 'A5' | 'A4';

const TEMPLATE_URLS: Record<InstructionLanguage, string> = {
  pl: 'assets/pdf-templates/instrukcja-aktywacji-pl.pdf',
  uk: 'assets/pdf-templates/instrukcja-aktywacji-uk.pdf',
  en: 'assets/pdf-templates/instrukcja-aktywacji-en.pdf',
};

// Template page size (A5 portrait, in PDF points). Identical across all language variants.
const TEMPLATE_WIDTH = 419.76;
const TEMPLATE_HEIGHT = 595.44;

// Coordinates (PDF points, origin bottom-left) of the two dashed placeholder boxes on the
// A5 template, measured from a 300dpi raster of the source PDF. Same across all languages.
const QR_BOX = { x: 61.92, y: 159.36, width: 83.04, height: 89.52 };
const CODE_BOX = { x: 262.32, y: 159.36, width: 82.8, height: 89.52 };

function buildSignUpUrl(code: string): string {
  return `https://zdamyprawko.pl/app/sign-up?code=${code}`;
}

@Injectable({ providedIn: 'root' })
export class InstructionPdfService {
  private readonly http = inject(HttpClient);
  private readonly templateBytesPromises: Partial<Record<InstructionLanguage, Promise<ArrayBuffer>>> = {};

  private loadTemplateBytes(language: InstructionLanguage): Promise<ArrayBuffer> {
    if (!this.templateBytesPromises[language]) {
      this.templateBytesPromises[language] = firstValueFrom(
        this.http.get(TEMPLATE_URLS[language], { responseType: 'arraybuffer' })
      );
    }
    return this.templateBytesPromises[language]!;
  }

  // Draws the QR code + manual-entry code onto `page`, at the template's placeholder boxes
  // shifted right by `xOffset` (used to address the left/right A5 slot on an A4 sheet).
  private async stampCode(
    pdfDoc: PDFDocument,
    page: PDFPage,
    font: PDFFont,
    ticket: IAccessTicketVm,
    xOffset: number
  ): Promise<void> {
    const qrDataUrl = await QRCode.toDataURL(buildSignUpUrl(ticket.code), {
      margin: 0,
      errorCorrectionLevel: 'M',
    });
    const qrImageBytes = Uint8Array.from(atob(qrDataUrl.split(',')[1]), (c) => c.charCodeAt(0));
    const qrImage = await pdfDoc.embedPng(qrImageBytes);

    const qrSize = Math.min(QR_BOX.width, QR_BOX.height) - 12;
    page.drawImage(qrImage, {
      x: xOffset + QR_BOX.x + (QR_BOX.width - qrSize) / 2,
      y: QR_BOX.y + (QR_BOX.height - qrSize) / 2,
      width: qrSize,
      height: qrSize,
    });

    const maxTextWidth = CODE_BOX.width - 12;
    let fontSize = 16;
    while (fontSize > 8 && font.widthOfTextAtSize(ticket.code, fontSize) > maxTextWidth) {
      fontSize -= 1;
    }
    const textWidth = font.widthOfTextAtSize(ticket.code, fontSize);
    page.drawText(ticket.code, {
      x: xOffset + CODE_BOX.x + (CODE_BOX.width - textWidth) / 2,
      y: CODE_BOX.y + CODE_BOX.height / 2 - fontSize / 2 + 2,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }

  async generateOne(ticket: IAccessTicketVm, language: InstructionLanguage = 'pl'): Promise<Uint8Array> {
    const templateBytes = await this.loadTemplateBytes(language);
    const pdfDoc = await PDFDocument.load(templateBytes);
    const page = pdfDoc.getPages()[0];
    const font = await pdfDoc.embedFont('Helvetica-Bold');
    await this.stampCode(pdfDoc, page, font, ticket, 0);
    return pdfDoc.save();
  }

  // A4 landscape sheets, two vertical A5 templates per sheet, filled with consecutive codes
  // (not duplicated). If the count is odd, the last sheet's second slot is left blank.
  async generateA4Batch(tickets: IAccessTicketVm[], language: InstructionLanguage): Promise<Uint8Array> {
    const templateBytes = await this.loadTemplateBytes(language);
    const pdfDoc = await PDFDocument.create();
    const [embeddedTemplate] = await pdfDoc.embedPdf(templateBytes);
    const font = await pdfDoc.embedFont('Helvetica-Bold');

    for (let i = 0; i < tickets.length; i += 2) {
      const page = pdfDoc.addPage([TEMPLATE_WIDTH * 2, TEMPLATE_HEIGHT]);
      page.drawPage(embeddedTemplate, { x: 0, y: 0, width: TEMPLATE_WIDTH, height: TEMPLATE_HEIGHT });
      await this.stampCode(pdfDoc, page, font, tickets[i], 0);

      const second = tickets[i + 1];
      if (second) {
        page.drawPage(embeddedTemplate, { x: TEMPLATE_WIDTH, y: 0, width: TEMPLATE_WIDTH, height: TEMPLATE_HEIGHT });
        await this.stampCode(pdfDoc, page, font, second, TEMPLATE_WIDTH);
      }
    }

    return pdfDoc.save();
  }

  fileName(ticket: IAccessTicketVm, index: number): string {
    return `zdamyPrawko_${this.datePart()}_${index}.pdf`;
  }

  private datePart(): string {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  async downloadOne(ticket: IAccessTicketVm, index: number, language: InstructionLanguage = 'pl'): Promise<void> {
    const bytes = await this.generateOne(ticket, language);
    this.triggerDownload(bytes, this.fileName(ticket, index));
  }

  async downloadBulk(
    tickets: IAccessTicketVm[],
    language: InstructionLanguage,
    format: InstructionFormat
  ): Promise<void> {
    if (format === 'A4') {
      const bytes = await this.generateA4Batch(tickets, language);
      this.triggerDownload(bytes, `zdamyPrawko_instrukcje_A4_${this.datePart()}.pdf`);
      return;
    }

    const zip = new JSZip();
    for (let i = 0; i < tickets.length; i++) {
      const bytes = await this.generateOne(tickets[i], language);
      zip.file(this.fileName(tickets[i], i + 1), bytes);
    }
    const zipBytes = await zip.generateAsync({ type: 'uint8array' });
    this.triggerDownload(zipBytes, `zdamyPrawko_instrukcje_${this.datePart()}.zip`);
  }

  private triggerDownload(bytes: Uint8Array, fileName: string): void {
    const blob = new Blob([bytes as BlobPart]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
